import { Button, Segmented } from 'antd';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChartColumn } from 'lucide-react';
import { parseAsNumberLiteral, parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { useState, type ReactNode } from 'react';
import {
  DUTY_ANALYTICS_DEFAULT_RANGE,
  DUTY_ANALYTICS_RANGES,
  addIstDays,
  type DutyAnalyticsDto,
  type DutyAnalyticsLayout,
  type DutyAnalyticsRange,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Loading, Skeleton } from '@/components/Skeleton';
import { StatusPill } from '@/components/StatusPill';
import { istTime } from '@/lib/analyticsReport';
import { ApiError, api } from '@/lib/api';
import {
  DUTY_ANALYTICS_PATH,
  customWindowOf,
  dutyAnalyticsHref,
  wayBackDivisionOf,
  windowParamOf,
  windowWords,
  type DutyAnalyticsWindowParam,
} from '@/lib/dutyAnalytics';
import { DA_CARD_GRID } from '@/lib/dutyAnalyticsLayout';
import { istNow } from '@/lib/istNow';
import { queryKeys } from '@/lib/queryKeys';
import { DUTY_ANALYTICS_COPY } from './copy';
import { CommissionerateLayout } from './components/CommissionerateLayout';
import { CustomRange } from './components/CustomRange';
import { DivisionLayout } from './components/DivisionLayout';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  DUTY ANALYTICS — who was asked for how many duties, who named them, who handed them down, who
 *  declared a short-fall or left one open, and who reached. [P7C §2]
 *
 *  ⚠️ ONE REQUEST PER (RANGE, NARROWING), AND BOTH ARE THE URL. `?range=` is 1, 7 or 30 IST days ending
 *  today (the server echoes the window it read, and the subtitle prints that echo, never the browser's
 *  clock); the default is not written into the URL (`clearOnDefault`), so a shared link does not bake
 *  in the default of the day it was copied. `keepPreviousData` keeps the last figures on screen while
 *  the next range loads, and the subtitle says so. Nothing polls: the figures change when an order is
 *  worked, which is not a clock.
 *
 *  ⚠️ THE LAYOUT IS THE SERVER'S ECHO, NEVER THE SEAT AND NEVER THE TILE REGISTRY. The server decides
 *  by POSITION (the commissionerate, or a division), and this page renders whichever static layout it
 *  answered. An exhaustive switch makes a third layout a compile error.
 *
 *  ⚠️ `?division=` IS SENT AS `divisionId` ONLY ONCE THE SERVER HAS ECHOED THE COMMISSIONERATE'S
 *  LAYOUT. A division's own page answers any `divisionId` with a 404, so a division nodal opening a link
 *  a colleague at the commissionerate shared must never send it. The cost, stated: a narrowed link
 *  opened cold makes two requests — the whole view, then the narrowed one.
 *
 *  ⚠️ ERRORS: a 4xx — the unit-level 403, a range past the order bound, a stale narrowing — prints the
 *  server's own sentence with no retry (a refusal is not transient, and `queryClient` never retries a
 *  4xx), and the range control stays usable above it. "Try again" is for a 5xx or no answer at all.
 *
 *  ⚠️ [amended P7C.1 §5] A CUSTOM WINDOW IS A FOURTH CHOICE, AND THE URL HOLDS IT AS `dateFrom`/`dateTo`. A preset
 *  ends today; a custom window ends today or earlier, never later, and covers at most thirty days — the largest
 *  preset, derived. One parse decides which is sent: a URL pair counts only when both dates exist and the shared
 *  rule (`dutyAnalyticsWindowRefusal`, against the IST day from `istNow()`) accepts it; anything else falls back to
 *  the preset, as `?range=5` always has. The request carries the preset OR the pair, never both, and "Custom" seeds
 *  its pair from the preset in force — `analyticsWindow`'s own arithmetic, so the figures do not move.
 *
 *  ⚠️ [amended P7C.1 §4] A DIVISION'S OWN PAGE, READ FROM THE COMMISSIONERATE, IS THIS PAGE WITH `asDivisionId` —
 *  rendered by `DivisionRoute.tsx` at `/duty-analytics/divisions/:id`. It ALWAYS sends the division and never a
 *  `divisionId`, renders the division's layout only when the server's echo IS that division, and keeps the
 *  commissionerate page's `?division=` for the way back alone. Whether the caller may read it is the server's:
 *  a division-level caller is refused for every id with one 404 sentence.
 *
 *  ⚠️ NO SCORE, RANK, RATE OR PERCENTAGE ANYWHERE (§2d) — `noScore.test.ts` scans every file of this
 *  feature. [amended P7C.1 §3] And ONE CENSUS OVERLAY on the page: `FigureInfo`'s `Modal`, one site however
 *  many render, swept by `e2e/fixtures/overlays.ts` — no other `Tooltip`, `Dropdown`, `Drawer`, `Modal`,
 *  `Sheet` or `Dialog`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;

const NUQS_OPTIONS = { clearOnDefault: true, history: 'replace' } as const;

/** [P7C.1 §5] The fourth choice beside the three presets. */
const CUSTOM = 'custom';
type RangeChoice = DutyAnalyticsRange | typeof CUSTOM;

const RANGE_OPTIONS: { value: RangeChoice; label: string }[] = [
  ...DUTY_ANALYTICS_RANGES.map((value) => ({ value, label: copy.ranges[value] })),
  { value: CUSTOM, label: copy.customRange },
];

/** The window's three URL keys, read and written as ONE state so a choice never leaves a range beside a pair. */
const WINDOW_PARSERS = {
  range: parseAsNumberLiteral(DUTY_ANALYTICS_RANGES).withDefault(DUTY_ANALYTICS_DEFAULT_RANGE),
  dateFrom: parseAsString,
  dateTo: parseAsString,
};

const layoutFor = (
  report: DutyAnalyticsDto,
  division: string,
  onDivision: (next: string | null) => void,
  asDivisionId: string | undefined,
): ReactNode => {
  /* ⚠️ The window the links carry is the server's ECHO — a preset, or the custom pair it read. */
  const range = windowParamOf(report.window);
  switch (report.layout) {
    case 'COMMISSIONERATE':
      return <CommissionerateLayout report={report} range={range} division={division} onDivision={onDivision} />;
    case 'DIVISION':
      return asDivisionId === undefined ? (
        <DivisionLayout report={report} range={range} />
      ) : (
        <DivisionLayout
          report={report}
          range={range}
          asDivisionId={asDivisionId}
          pageDivision={wayBackDivisionOf(division)}
        />
      );
    default: {
      const unreachable: never = report.layout;
      return unreachable;
    }
  }
};

const DutyAnalyticsPage = ({ asDivisionId }: { asDivisionId?: string }) => {
  const [urlWindow, setUrlWindow] = useQueryStates(WINDOW_PARSERS, NUQS_OPTIONS);
  const [division, setDivision] = useQueryState('division', parseAsString.withDefault('').withOptions(NUQS_OPTIONS));

  /*
   * ⚠️ [P7C.1 §5d] TODAY IS THE IST DAY, FROM THE ONE DOOR (`istNow`), and every comparison against it is a
   * calendar-day string. A pair the shared rule refuses is not sent: the preset in the URL, or seven, is.
   */
  const today = istNow().toISODate() ?? '';
  const custom = customWindowOf(urlWindow.dateFrom, urlWindow.dateTo, today);
  const chosen: DutyAnalyticsWindowParam = custom ?? urlWindow.range;
  const sent = custom ?? { range: urlWindow.range };

  /*
   * ⚠️ THE LAYOUT THE SERVER LAST ECHOED — adjusted DURING RENDER (React's documented pattern,
   * `useServerTable`'s precedent), so the narrowed request is the very next one rather than an effect
   * and a frame later. A division read AS it never narrows: its `?division=` is the way back alone.
   */
  const [layoutSeen, setLayoutSeen] = useState<DutyAnalyticsLayout | null>(null);
  const divisionId =
    asDivisionId === undefined && layoutSeen === 'COMMISSIONERATE' && division !== '' ? division : null;
  const asView = asDivisionId === undefined ? {} : { asDivisionId };

  const report = useQuery({
    queryKey: queryKeys.dutyAnalytics.view({ ...sent, divisionId, ...asView }),
    queryFn: ({ signal }) =>
      api.get<DutyAnalyticsDto>(DUTY_ANALYTICS_PATH, {
        query: { ...sent, ...(divisionId === null ? {} : { divisionId }), ...asView },
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  const data = report.data;
  if (data !== undefined && data.layout !== layoutSeen) setLayoutSeen(data.layout);

  /*
   * ⚠️ [P7C.1 §4] THE ECHO IS TRUSTED ONLY WHEN IT IS THE DIVISION ASKED FOR. An API that dropped the key would
   * answer with the caller's own page, and its figures would be painted under this division's URL.
   */
  const mismatch =
    asDivisionId !== undefined &&
    data !== undefined &&
    (data.layout !== 'DIVISION' || data.viewer?.id !== asDivisionId);
  const viewedName = asDivisionId !== undefined && data !== undefined && !mismatch ? (data.viewer?.name ?? null) : null;

  const chooseRange = (value: RangeChoice): void => {
    if (value !== CUSTOM) {
      void setUrlWindow({ range: value, dateFrom: null, dateTo: null });
      return;
    }
    if (today === '') return;
    void setUrlWindow({ range: null, dateFrom: addIstDays(today, -(urlWindow.range - 1)), dateTo: today });
  };

  const subtitle =
    data === undefined
      ? undefined
      : report.isPlaceholderData
        ? copy.updating
        : report.isError
          ? copy.refreshFailed(windowWords(data.window))
          : copy.updated(windowWords(data.window), istTime(data.generatedAt));

  const error = report.error;
  /* A 4xx is the server's refusal, said in its own sentence; anything else is "could not answer". */
  const refusal = error instanceof ApiError && error.status >= 400 && error.status < 500 ? error.message : null;

  return (
    <div className="flex flex-col gap-6" data-testid="duty-analytics-root" data-layout={data?.layout}>
      <PageHeader
        icon={ChartColumn}
        title={viewedName === null ? copy.title : copy.asViewTitle(viewedName)}
        {...(asDivisionId === undefined
          ? {}
          : {
              parent: {
                label: copy.rowsParent,
                to: dutyAnalyticsHref(chosen, wayBackDivisionOf(division)),
              },
            })}
        subtitle={subtitle}
        filters={
          custom === null ? undefined : (
            <CustomRange
              key={`${custom.dateFrom}|${custom.dateTo}`}
              value={custom}
              today={today}
              onChange={(next) => void setUrlWindow({ range: null, ...next })}
            />
          )
        }
        actions={
          <>
            {/*
              ⚠️ [P7C §2] THE PREVIEW BADGE IS ONE LINE HOLDING ITS OWN LITERAL — deleting that line deletes the
              badge, and `noScore.test.ts` pins it there. Prettier would break any element with two attributes
              and text children onto three lines (measured), which leaves an empty pill behind a one-line
              delete; the ignore below is that pin, and nothing else in this file is exempt.
            */}
            {/* prettier-ignore */}
            <StatusPill tone="info" data-testid="duty-analytics-preview">Initial preview of analytics</StatusPill>
            <Segmented<RangeChoice>
              aria-label={copy.rangeLabel}
              data-testid="duty-analytics-range"
              value={custom === null ? urlWindow.range : CUSTOM}
              options={RANGE_OPTIONS}
              onChange={chooseRange}
            />
          </>
        }
      />

      {viewedName !== null && (
        <p className="text-sm text-muted-foreground" data-testid="duty-analytics-viewing">
          {copy.viewingAs(viewedName)}
        </p>
      )}

      {data !== undefined ? (
        mismatch ? (
          <div data-testid="duty-analytics-mismatch">
            <EmptyState title={copy.asViewMismatch} />
          </div>
        ) : (
          layoutFor(data, division, (next) => void setDivision(next), asDivisionId)
        )
      ) : report.isError ? (
        refusal !== null ? (
          /* ⚠️ `EmptyState` forwards no `data-testid` — the StatusPill lesson — so the handle is a wrapper. */
          <div data-testid="duty-analytics-refused">
            <EmptyState title={refusal} />
          </div>
        ) : (
          <EmptyState
            title={copy.errorTitle}
            description={copy.errorDescription}
            action={<Button onClick={() => void report.refetch()}>{copy.retry}</Button>}
          />
        )
      ) : (
        /*
         * ⚠️ ONE PAGE-LEVEL SILHOUETTE, AND IT IS THE REAL SHAPE: both layouts open with four cards and
         * a table, so this is not a guess that reflows into something else ([P6a]'s rule).
         */
        <div data-testid="duty-analytics-loading">
          <Loading label={copy.loadingPage}>
            <div className="flex flex-col gap-6">
              <div className={DA_CARD_GRID}>
                {[0, 1, 2, 3].map((index) => (
                  <Skeleton key={index} className="h-28 w-full" />
                ))}
              </div>
              <Skeleton className="h-64 w-full" />
            </div>
          </Loading>
        </div>
      )}
    </div>
  );
};

export default DutyAnalyticsPage;
